import React, { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { di } from 'react-magnetic-di';
import { graphql, useFragment } from 'react-relay';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import CrossIcon from '@atlaskit/icon/core/cross';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import type { PositionType } from '@atlaskit/tooltip/types';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import type {
	ActionCallbacks,
	ActionStatusProps,
} from '@atlassian/conversation-assistant-message-actions-api/types';
import { classifySourceUrl } from '@atlassian/conversation-assistant-utils/classifySourceUrl';

import type { UpdateWorkItemAllFieldTypesAction } from '../../../../common/types';
import { FieldTooltipContent } from '../../../../common/ui/FieldTooltipContent';
import { useFieldTooltip } from '../../../../common/utils/useFieldTooltip';
import { getActionData } from '../../utils';

import type {
	cardFieldsList_updateWorkItemAllFieldTypesActionRenderer$data,
	cardFieldsList_updateWorkItemAllFieldTypesActionRenderer$key,
} from './__generated__/cardFieldsList_updateWorkItemAllFieldTypesActionRenderer.graphql';
import { ColorField } from './color-field';
import { ColorFieldIcon } from './color-field/ColorFieldIcon';
import { CommentField } from './comment-field';
import { CommentFieldIcon } from './comment-field/CommentFieldIcon';
import { DatePickerField } from './date-picker-field';
import { DatePickerFieldIcon } from './date-picker-field/DatePickerFieldIcon';
import { DateTimePickerField } from './date-time-picker-field';
import { DateTimePickerFieldIcon } from './date-time-picker-field/DateTimePickerFieldIcon';
import { FlagField } from './flag-field';
import { FlagFieldIcon } from './flag-field/FlagFieldIcon';
import { GenericField } from './generic-field';
import type { genericField_updateWorkItemAllFieldTypesActionRenderer$key as GenericFieldType } from './generic-field/__generated__/genericField_updateWorkItemAllFieldTypesActionRenderer.graphql';
import { GenericFieldIcon } from './generic-field/GenericFieldIcon';
import { IssueLinksField } from './issue-links-field';
import { IssueLinksFieldIcon } from './issue-links-field/IssueLinksFieldIcon';
import { LabelsField } from './labels-field';
import { LabelsFieldIcon } from './labels-field/LabelsFieldIcon';
import { NumberField } from './number-field';
import { NumberFieldIcon } from './number-field/NumberFieldIcon';
import { ParentIssueField } from './parent-issue-field';
import { ParentIssueFieldIcon } from './parent-issue-field/ParentIssueFieldIcon';
import { PriorityField } from './priority-field';
import { PriorityFieldIcon } from './priority-field/PriorityFieldIcon';
import { ReasoningTooltip } from './reasoning-tooltip';
import { RichTextField } from './rich-text-field';
import { RichTextFieldIcon } from './rich-text-field/RichTextFieldIcon';
import { SingleLineTextField } from './single-line-text-field';
import { SingleLineTextFieldIcon } from './single-line-text-field/SingleLineTextFieldIcon';
import { SingleSelectUserPickerField } from './single-select-user-picker-field';
import { SingleSelectUserPickerFieldIcon } from './single-select-user-picker-field/SingleSelectUserPickerFieldIcon';
import { SprintField } from './sprint-field';
import { SprintFieldIcon } from './sprint-field/SprintFieldIcon';
import { StatusField } from './status-field';
import { StatusFieldIcon } from './status-field/StatusFieldIcon';
import { StatusIcon } from './status-icon';
import { TeamViewField } from './team-view-field';
import { TeamViewFieldIcon } from './team-view-field/TeamViewFieldIcon';
import { TimeSpentField } from './time-tracking-fields/time-spent-field';
import { TimeSpentFieldIcon } from './time-tracking-fields/time-spent-field/TimeSpentFieldIcon';
import { TimeTrackingField } from './time-tracking-fields/time-tracking-field';
import { TimeTrackingFieldIcon } from './time-tracking-fields/time-tracking-field/TimeTrackingFieldIcon';
import { UsersField } from './users-field';
import { UsersFieldIcon } from './users-field/UsersFieldIcon';

const styles = cssMap({
	root: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-start',
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		paddingTop: token('space.050'),
		paddingBottom: token('space.050'),
		transition: '200ms',
		cursor: 'default',
		borderRadius: token('radius.small'),
		outlineColor: token('color.border.focused'),
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
		},
	},
	statusIconContainer: {
		display: 'flex',
		alignItems: 'center',
		flex: '0 0 auto',
		marginRight: token('space.050'),
	},
	fieldsContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.050'),
		flex: '1 1 auto',
		minWidth: '0px',
		overflow: 'hidden',
		textAlign: 'left',
	},
	// This allows the buttons to be keyboard accessible, but also be hidden when we are not focused/hovered
	actionButtonsHidden: {
		opacity: 0,
	},
});

type JiraField = NonNullable<
	NonNullable<
		cardFieldsList_updateWorkItemAllFieldTypesActionRenderer$data['fieldsByIdOrAlias']
	>[number]
>;

export type Props = {
	cloudId: string;
	queryRef: cardFieldsList_updateWorkItemAllFieldTypesActionRenderer$key;
	action: UpdateWorkItemAllFieldTypesAction;
	isBulkActioning?: boolean;
	showActionButtons?: boolean;
	onInvalidAction?: (action: UpdateWorkItemAllFieldTypesAction) => void;
	tooltipPosition?: PositionType;
} & ActionCallbacks &
	ActionStatusProps;

/**
 * Renders a list of Jira issue fields for updating work item fields in a card UI.
 * Displays appropriate field components based on the field type, along with status icons,
 * tooltips for reasoning, and action buttons for confirming or cancelling the update.
 * Handles special cases such as the comment field, which is not part of the standard fields list.
 *
 * @param queryRef - The Relay fragment reference for the Jira issue.
 * @param cloudId - The Jira cloud instance ID.
 * @param action - The action object containing field update data and status.
 * @param onConfirm - Callback invoked when the user confirms the update.
 * @param onCancel - Callback invoked when the user cancels the update.
 * @param getStatus - Function to retrieve the current status of the action.
 * @param getReadonly - Function to determine if the action is read-only.
 *
 * @returns A React element rendering the fields, status icon, tooltips, and action buttons,
 *          or null if there are no fields to render.
 */
export const CardFieldsList = ({
	queryRef,
	cloudId,
	action,
	onConfirm,
	onCancel,
	getStatus,
	getReadonly,
	isBulkActioning = false,
	showActionButtons = true,
	onInvalidAction,
	tooltipPosition = 'left',
}: Props): React.JSX.Element | null => {
	di(
		SingleLineTextField,
		RichTextField,
		SingleSelectUserPickerField,
		UsersField,
		StatusField,
		FlagField,
		LabelsField,
		PriorityField,
		DatePickerField,
		DateTimePickerField,
		ParentIssueField,
		CommentField,
		GenericField,
		NumberField,
		ColorField,
		useFieldTooltip,
		TeamViewField,
		IssueLinksField,
	);

	const { sendAnalyticsEvent } = useAnalytics();
	const { fieldKey, fieldReasoning, sourceUrl } = getActionData(action);

	const {
		tooltipContent,
		registerTooltipProvider,
		rootHoverHandlers,
		focusHandlers,
		isHovered,
		isFocused,
	} = useFieldTooltip(fieldKey);

	// Persist last tooltip ReactNode so moving cursor to tooltip overlay doesn't lose field-specific content.
	const [persistedTooltipContent, setPersistedTooltipContent] = useState<ReactNode>(null);

	useEffect(() => {
		if (tooltipContent != null) {
			setPersistedTooltipContent(tooltipContent);
		}
	}, [tooltipContent, fieldKey]);

	const status = getStatus(action);
	const readonly = getReadonly(action);

	const isSubmitting = status === 'started' || action?.status === 'started';
	const isFailed = status === 'errored' || action?.status === 'errored';
	const isDisabled = status !== 'idle' || readonly;
	const isCompleted = status === 'finished' || action?.status === 'finished';
	const isCancelled = status === 'cancelled' || action?.status === 'cancelled';
	const isIdle = status === 'idle' && action?.status === 'planned';

	// When an action is completed, we want to hide the icons and buttons when a user unhovers or focuses away from the action (aside from the success
	// icon, which is always visible after the action has been accepted). However, `isFocused` remains true even if we unfocus on the action after
	// it has been accepted or cancelled. Temporary fix for TEAM25EU until we find a proper fix to the focus issue.
	const isActionCompleted = isCompleted || isCancelled || isFailed;
	const issue = useFragment(
		graphql`
			fragment cardFieldsList_updateWorkItemAllFieldTypesActionRenderer on JiraIssue
			@argumentDefinitions(
				fieldIdsOrAliases: { type: "[String!]!" }
				sprintAris: { type: "[ID!]!" }
			) {
				key
				fieldsByIdOrAlias(idsOrAliases: $fieldIdsOrAliases, ignoreMissingFields: true)
					@optIn(to: "JiraIssueFieldsByIdOrAlias") {
					__typename
					id
					fieldId
					type
					...singleLineTextField_updateWorkItemAllFieldTypesActionRenderer
						@dangerously_unaliased_fixme
					...richTextField_updateWorkItemAllFieldTypesActionRenderer @dangerously_unaliased_fixme
					...singleSelectUserPickerField_updateWorkItemAllFieldTypesActionRenderer
						@dangerously_unaliased_fixme
					...usersField_updateWorkItemAllFieldTypesActionRenderer
					...statusField_updateWorkItemAllFieldTypesActionRenderer @dangerously_unaliased_fixme
					...priorityField_updateWorkItemAllFieldTypesActionRenderer @dangerously_unaliased_fixme
					...labelsField_updateWorkItemAllFieldTypesActionRenderer @dangerously_unaliased_fixme
					...flagField_updateWorkItemAllFieldTypesActionRenderer @dangerously_unaliased_fixme
					...datePickerField_updateWorkItemAllFieldTypesActionRenderer @dangerously_unaliased_fixme
					...dateTimePickerField_updateWorkItemAllFieldTypesActionRenderer
						@dangerously_unaliased_fixme
					...parentIssueField_updateWorkItemAllFieldTypesActionRenderer @dangerously_unaliased_fixme
					...numberField_updateWorkItemAllFieldTypesActionRenderer @dangerously_unaliased_fixme
					...timeTrackingField_updateWorkItemAllFieldTypesActionRenderer
						@dangerously_unaliased_fixme
					...timeSpentField_updateWorkItemAllFieldTypesActionRenderer @dangerously_unaliased_fixme
					...colorField_updateWorkItemAllFieldTypesActionRenderer @dangerously_unaliased_fixme
					...teamViewField_updateWorkItemAllFieldTypesActionRenderer @dangerously_unaliased_fixme
					...genericField_updateWorkItemAllFieldTypesActionRenderer
					...sprintField_updateWorkItemAllFieldTypesActionRenderer
						@dangerously_unaliased_fixme
						@arguments(sprintAris: $sprintAris)
				}
			}
		`,
		queryRef,
	);

	// This allows for custom fields types to be tracked in the analytics event.
	const fieldType =
		issue?.fieldsByIdOrAlias?.filter((field) => field?.fieldId === fieldKey)?.[0]?.type ?? fieldKey;

	const analyticsAttributes = {
		fieldType,
		hasReasoning: !!fieldReasoning,
		hasSourceUrl: !!sourceUrl,
		...(fg('add-source-in-update-workitem-analytics')
			? { sourceUrlType: classifySourceUrl(sourceUrl) }
			: {}),
	};

	useEffect(() => {
		if (isIdle) {
			sendAnalyticsEvent({
				action: 'rendered',
				actionSubject: 'suggestion',
				actionSubjectId: 'updateWorkField',
				attributes: {
					...analyticsAttributes,
				},
			});
		}
		// Empty dependency array to ensure that we only fire the analytics event once when the suggestion renders.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const fields = issue?.fieldsByIdOrAlias;
	const hasFields = Array.isArray(fields) && fields.length > 0;

	const filteredFields = useMemo(
		() =>
			hasFields
				? fields
						.filter((field): field is JiraField => Boolean(field))
						.filter(
							(field) =>
								field?.fieldId === fieldKey ||
								// Edge case with the custom time spent field - we still want to use timetracking to fetch its data.
								(field?.fieldId === 'timetracking' && fieldKey === 'timespent'),
						)
				: [],
		[hasFields, fields, fieldKey],
	);

	const onInvalidActionCallback = useCallback(
		() => onInvalidAction?.(action),
		[onInvalidAction, action],
	);

	const fieldsRegistry = useMemo(() => {
		const registry = filteredFields
			.map((field) => {
				switch (field?.__typename) {
					case 'JiraSingleLineTextField':
						return {
							key: field?.id,
							icon: <SingleLineTextFieldIcon />,
							component: (
								<SingleLineTextField
									key={field.id}
									action={action}
									queryRef={field}
									registerTooltipProvider={registerTooltipProvider}
								/>
							),
						};
					case 'JiraRichTextField':
						return {
							key: field?.id,
							icon: <RichTextFieldIcon />,
							component: (
								<RichTextField
									key={field.id}
									action={action}
									queryRef={field}
									registerTooltipProvider={registerTooltipProvider}
								/>
							),
						};
					case 'JiraSingleSelectUserPickerField':
						return {
							key: field?.id,
							icon: <SingleSelectUserPickerFieldIcon />,
							component: (
								<SingleSelectUserPickerField
									key={field.id}
									action={action}
									queryRef={field}
									onInvalidAction={onInvalidActionCallback}
								/>
							),
						};
					case 'JiraMultipleSelectUserPickerField':
					case 'JiraPeopleField':
						return {
							key: field?.id,
							icon: <UsersFieldIcon />,
							component: (
								<UsersField
									key={field.id}
									action={action}
									queryRef={field}
									registerTooltipProvider={registerTooltipProvider}
									onInvalidAction={onInvalidActionCallback}
								/>
							),
						};
					case 'JiraStatusField':
						return {
							key: field?.id,
							icon: <StatusFieldIcon />,
							component: (
								<StatusField
									key={field.id}
									action={action}
									queryRef={field}
									onInvalidAction={onInvalidActionCallback}
								/>
							),
						};
					case 'JiraPriorityField':
						return {
							key: field?.id,
							icon: <PriorityFieldIcon />,
							component: (
								<PriorityField
									key={field.id}
									action={action}
									queryRef={field}
									{...(FeatureGates.getExperimentValue(
										'jira-ai-update-workitem-field-action-details-exp',
										'isEnabled',
										false,
									)
										? { registerTooltipProvider }
										: {})}
								/>
							),
						};
					case 'JiraLabelsField':
						return {
							key: field?.id,
							icon: <LabelsFieldIcon />,
							component: (
								<LabelsField
									key={field.id}
									action={action}
									queryRef={field}
									registerTooltipProvider={registerTooltipProvider}
								/>
							),
						};
					case 'JiraFlagField':
						return {
							key: field?.id,
							icon: <FlagFieldIcon />,
							component: <FlagField key={field.id} action={action} queryRef={field} />,
						};
					case 'JiraDatePickerField':
						return {
							key: field?.id,
							icon: <DatePickerFieldIcon />,
							component: <DatePickerField key={field.id} action={action} queryRef={field} />,
						};
					case 'JiraDateTimePickerField':
						return {
							key: field?.id,
							icon: <DateTimePickerFieldIcon />,
							component: <DateTimePickerField key={field.id} action={action} queryRef={field} />,
						};
					case 'JiraParentIssueField':
						return {
							key: field?.id,
							icon: <ParentIssueFieldIcon />,
							component: (
								<ParentIssueField
									key={field.id}
									cloudId={cloudId}
									action={action}
									queryRef={field}
									registerTooltipProvider={registerTooltipProvider}
									onInvalidAction={onInvalidActionCallback}
								/>
							),
						};
					case 'JiraNumberField':
						return {
							key: field?.id,
							icon: <NumberFieldIcon />,
							component: (
								<NumberField
									key={field.id}
									action={action}
									queryRef={field}
									registerTooltipProvider={registerTooltipProvider}
								/>
							),
						};
					case 'JiraColorField':
						return {
							key: field?.id,
							icon: <ColorFieldIcon />,
							component: (
								<ColorField
									key={field.id}
									action={action}
									queryRef={field}
									registerTooltipProvider={registerTooltipProvider}
								/>
							),
						};
					case 'JiraTimeTrackingField':
						if (fieldKey === 'timespent') {
							return {
								key: field?.id,
								icon: <TimeSpentFieldIcon />,
								component: (
									<TimeSpentField
										key={field.id}
										action={action}
										queryRef={field}
										registerTooltipProvider={registerTooltipProvider}
									/>
								),
							};
						}
						return {
							key: field?.id,
							icon: <TimeTrackingFieldIcon />,
							component: (
								<TimeTrackingField
									key={field.id}
									action={action}
									queryRef={field}
									registerTooltipProvider={registerTooltipProvider}
								/>
							),
						};
					case 'JiraTeamViewField':
						return {
							key: field?.id,
							icon: <TeamViewFieldIcon />,
							component: (
								<TeamViewField
									cloudId={cloudId}
									key={field.id}
									action={action}
									queryRef={field}
									registerTooltipProvider={registerTooltipProvider}
									onInvalidAction={onInvalidActionCallback}
								/>
							),
						};
					case 'JiraSprintField':
						return {
							key: field?.id,
							icon: <SprintFieldIcon />,
							component: (
								<SprintField
									key={field.id}
									action={action}
									queryRef={field}
									registerTooltipProvider={registerTooltipProvider}
								/>
							),
						};
					default:
						return {
							key: field?.id,
							icon: <GenericFieldIcon />,
							component: (
								<GenericField
									key={field?.id}
									action={action}
									queryRef={field as GenericFieldType}
									registerTooltipProvider={registerTooltipProvider}
								/>
							),
						};
				}
			})
			.filter(Boolean); // Filter out null or undefined components

		// Ensure CommentField is added if action.data.fieldKey is 'comment'
		if (fieldKey === 'comment') {
			const key = `${action.data.updates[0]?.issueIdOrKey}-${fieldKey}-${action.invocationId}`;
			registry.push({
				key,
				icon: <CommentFieldIcon />,
				component: (
					<CommentField
						key={key}
						action={action}
						registerTooltipProvider={registerTooltipProvider}
					/>
				),
			});
		}

		// Ensure IssueLinksField is added if action.data.fieldKey is 'issuelinks' as it is not a traditional list field.
		if (fieldKey === 'issuelinks') {
			const key = `${action.data.updates[0]?.issueIdOrKey}-${fieldKey}-${action.invocationId}`;
			registry.push({
				key,
				icon: <IssueLinksFieldIcon />,
				component: (
					<IssueLinksField
						key={key}
						action={action}
						registerTooltipProvider={registerTooltipProvider}
						issueKey={issue?.key}
					/>
				),
			});
		}

		return registry;
	}, [
		action,
		cloudId,
		filteredFields,
		registerTooltipProvider,
		fieldKey,
		onInvalidActionCallback,
		issue?.key,
	]);

	if (!action || (fieldsRegistry.length === 0 && fieldKey !== 'comment')) {
		return null;
	}

	if (isCancelled) {
		return null;
	}

	const effectiveTooltipContent =
		tooltipContent ??
		persistedTooltipContent ??
		(fieldReasoning ? <FieldTooltipContent reasoning={fieldReasoning} /> : undefined);

	return (
		<>
			{fieldsRegistry.map(({ key, icon, component }) => (
				<Tooltip
					key={`tooltip-${key}`}
					component={ReasoningTooltip}
					content={effectiveTooltipContent}
					position={tooltipPosition}
					delay={0}
					canAppear={() => effectiveTooltipContent !== undefined}
				>
					{({ ...tooltipProps }) => (
						<Box
							{...tooltipProps}
							{...rootHoverHandlers}
							xcss={styles.root}
							tabIndex={0}
							onFocus={(e: React.FocusEvent<HTMLDivElement>) => {
								tooltipProps.onFocus?.(e);
								focusHandlers.onFocus?.();
							}}
							onBlur={(e: React.FocusEvent<HTMLDivElement>) => {
								tooltipProps.onBlur?.(e);
								focusHandlers.onBlur?.();
							}}
							// This prevents `isFocused` from being set to true when the user clicks on the Box. Without this,
							// a user could click on this Box (to no effect), and the status icons + action buttons would still be
							// shown when they hover away.
							onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => {
								e.preventDefault();
								e.stopPropagation();
							}}
							testId="update-work-item-all-fields-action-renderer.ui.card-fields-list"
						>
							<Box xcss={styles.statusIconContainer}>
								<StatusIcon
									showIcon={isActionCompleted ? isHovered : isHovered || isFocused}
									icon={icon}
									isCompleted={isCompleted}
									isLoading={isSubmitting}
									isFailed={isFailed}
									isBulkActioning={isBulkActioning}
								/>
							</Box>
							<Box xcss={styles.fieldsContainer}>{component}</Box>
							{showActionButtons && (
								<Inline
									testId="update-work-item-all-fields-action-renderer.ui.card-fields-list.action-buttons"
									xcss={
										!(isActionCompleted ? isHovered : isHovered || isFocused) &&
										styles.actionButtonsHidden
									}
									space="space.050"
									{...focusHandlers}
								>
									<IconButton
										icon={CheckMarkIcon}
										label="Accept"
										spacing="compact"
										appearance="subtle"
										onClick={() => {
											onConfirm(action);
											sendAnalyticsEvent({
												action: 'clicked',
												actionSubject: 'button',
												actionSubjectId: 'updateWorkAccept',
												attributes: {
													...analyticsAttributes,
													bulkActioned: false,
												},
											});
										}}
										isDisabled={isDisabled || isSubmitting || isBulkActioning}
									/>
									<IconButton
										icon={CrossIcon}
										label="Dismiss"
										spacing="compact"
										appearance="subtle"
										onClick={() => {
											onCancel(action);
											sendAnalyticsEvent({
												action: 'clicked',
												actionSubject: 'button',
												actionSubjectId: 'updateWorkDismiss',
												attributes: {
													...analyticsAttributes,
													bulkActioned: false,
												},
											});
										}}
										isDisabled={isDisabled || isSubmitting || isBulkActioning}
									/>
								</Inline>
							)}
						</Box>
					)}
				</Tooltip>
			))}
		</>
	);
};
